import React, { useCallback, useState } from 'react';
import {
  Card, CardContent, Typography, Box, List, ListItem,
  ListItemIcon, Chip, Collapse, IconButton, Tooltip,
  Avatar, Divider, CircularProgress, Stack
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import UndoIcon from '@mui/icons-material/Undo';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SearchIcon from '@mui/icons-material/Search';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import ShowerIcon from '@mui/icons-material/Shower';
import SanitizerIcon from '@mui/icons-material/Sanitizer';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import { RecentService } from '../Types/Storage';
import { useNavigate } from 'react-router-dom';

interface RecentServicesWidgetProps {
  services: RecentService[];
  loading?: boolean;
}

const RecentServicesWidget: React.FC<RecentServicesWidgetProps> = ({
  services,
  loading = false
}) => {
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  const handleServiceClick = useCallback((service: RecentService) => {
    if (!service?.type || service.id === undefined || service.id === null) {
      return;
    }
    const sheetType = service.type;
    const sheetId = String(service.id);
    const searchParams = new URLSearchParams({ sheetType, sheetId });
    navigate(
      { pathname: '/work-history', search: `?${searchParams.toString()}` },
      { state: { sheetType, sheetId, id: service.id } }
    );
  }, [navigate]);

  const getServiceIcon = (type: string) => {
    switch (type) {
      case 'sheets': return <WarehouseIcon sx={{ color: "#940000" }} />;
      case 'chemAppService': return <WarehouseIcon sx={{ color: "#EAA221" }} />;
      case 'preseason': return <UndoIcon sx={{ color: "#00796b" }} />;
      case 'refrigeration': return <AcUnitIcon sx={{ color: "#0288d1" }} />;
      case 'salesorders': return <ReceiptIcon sx={{ color: "#546e7a" }} />;
      case 'chemAppInspections': return <SearchIcon sx={{ color: "#5d4037" }} />;
      case 'inspections': return <SearchIcon sx={{ color: "#5d4037" }} />;
      case 'freshpack': return <PrecisionManufacturingIcon sx={{ color: "#388e3c" }} />;
      case 'chemAppAgreement': return <CloudSyncIcon sx={{ color: "#7b1fa2" }} />;
      case 'sprayBarAgreement': return <ShowerIcon sx={{ color: "#0097a7" }} />;
      case 'disinfectantAgreement': return <SanitizerIcon sx={{ color: "#689f38" }} />;
      default: return <BuildIcon color="primary" />;
    }
  };

  const getServiceTypeDisplay = (type: string) => {
    switch (type) {
      case 'sheets': return 'Service';
      case 'preseason': return 'Pre-Season';
      case 'refrigeration': return 'Refrigeration';
      case 'salesorders': return 'Sales Order';
      case 'chemappinspections': return 'Chem App Inspection';
      case 'inspections': return 'Inspection';
      case 'freshpack': return 'Fresh Pack';
      case 'chemAppAgreement': return 'Custom Application';
      case 'sprayBarAgreement': return 'Spray Bar Service';
      case 'disinfectantAgreement': return 'Disinfectant Service';
      default: return 'Service';
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} week${Math.floor(diffInDays / 7) === 1 ? '' : 's'} ago`;
    if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} month${Math.floor(diffInDays / 30) === 1 ? '' : 's'} ago`;
    return `${Math.floor(diffInDays / 365)} year${Math.floor(diffInDays / 365) === 1 ? '' : 's'} ago`;
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Recent Services
            </Typography>
            {services.length > 0 && (
              <Chip
                label={services.length}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Loading recent services...
                </Typography>
              </Stack>
            </Box>
          ) : services.length === 0 ? (
            <Box textAlign="center" py={4}>
              <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                No recent services found
              </Typography>
              <Typography variant="caption" color="text.disabled">
                Services will appear here once work has been performed at this location
              </Typography>
            </Box>
          ) : (
            <>
              <List
                sx={{
                  p: 0,
                  maxHeight: '520px',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.5)',
                    },
                  },
                }}
              >
                {services.map((service, index) => (
                  <React.Fragment key={service.id}>
                    <ListItem
                      onClick={() => handleServiceClick(service)}
                      sx={{
                        px: 0,
                        py: 1.5,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'transparent',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          bgcolor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 45 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider'
                          }}
                        >
                          {getServiceIcon(service.type)}
                        </Avatar>
                      </ListItemIcon>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="bold"
                            sx={{ flex: 1 }}
                          >
                            {service.title}
                          </Typography>
                          <Chip
                            label={getServiceTypeDisplay(service.type)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {service.description}
                        </Typography>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Tooltip title={new Date(service.dateServiced).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}>
                            <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                              {formatTimeAgo(new Date(service.dateServiced))}
                            </Typography>
                          </Tooltip>
                        </Box>
                        {service.creator && (
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {service.creator}
                            </Typography>
                            {service.po && (
                              <>
                                <Typography variant="caption" color="text.secondary">
                                  •
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  PO: {service.po}
                                </Typography>
                              </>
                            )}
                          </Box>
                        )}
                      </Box>
                    </ListItem>
                    {index < services.length - 1 && (
                      <Divider variant="inset" component="li" sx={{ ml: 6 }} />
                    )}
                  </React.Fragment>
                ))}
              </List>
            </>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default RecentServicesWidget;
